// Premium CCG route config for: /maidenhead-to-heathrow
// Keep reusable layout in CcgRoutePremiumPage; keep route-specific SEO/content here.

import React from "react";
import { Link } from "react-router-dom";
import CcgRoutePremiumPage from "../components/CcgRoutePremiumPage";
import PostcodeQuotenew from "./PostcodeQuotenew";
import "./CcgRoutePremium.css";

const cardGrid = (className, cards) => (
  <div className={className}>
    {cards.map((card) => (
      <article className={card.className || ""} key={card.title}>
        <h3>{card.title}</h3>
        {card.body ? <p>{card.body}</p> : null}
        {card.items ? (
          <ul>
            {card.items.map((item) => <li key={item}>{item}</li>)}
          </ul>
        ) : null}
        {card.link ? (
          <Link to={card.link.to} className={card.link.className || "route-link-inline"}>
            {card.link.label}
          </Link>
        ) : null}
        {card.links ? (
          <div className="route-link-btn-row">
            {card.links.map((link) => (
              <Link to={link.to} className={link.className || "route-link-btn"} key={link.to}>
                {link.label}
              </Link>
            ))}
          </div>
        ) : null}
      </article>
    ))}
  </div>
);

const maidenheadToHeathrowRoute = {
  id: "maidenhead-to-heathrow",
  path: "/maidenhead-to-heathrow",
  liveStripVariant: "slough",
  seo: {
    title: "Maidenhead to Heathrow Taxi - Fixed-Price Airport Transfers | CheapCabGrab",
    description:
      "Book a fixed-price taxi from Maidenhead (SL6) to Heathrow. Instant online quote, licensed drivers, flight tracking, 24/7 support and no surge pricing with CheapCabGrab.",
    keywords:
      "Maidenhead to Heathrow taxi, taxi to Heathrow Airport from Maidenhead, SL6 taxi to Heathrow, Maidenhead airport transfer, CheapCabGrab",
    canonical: "https://www.cheapcabgrab.com/maidenhead-to-heathrow",
    ogTitle: "Maidenhead to Heathrow Taxi - Fixed Airport Transfers SL6",
    ogDescription:
      "Reliable Maidenhead to Heathrow taxis with fixed fares, licensed drivers and live flight tracking. Book online in seconds with CheapCabGrab.",
    serviceName: "CheapCabGrab - Maidenhead to Heathrow Taxi",
    areaServed: ["Heathrow Airport", "Maidenhead SL6", "Cookham", "Taplow", "Bray"],
    offerCatalogName: "Maidenhead to Heathrow Taxi Routes",
    offerName: "Maidenhead to Heathrow Airport fixed-price taxi",
    offerDescription:
      "Fixed-price taxi transfers from Maidenhead (SL6) to Heathrow Airport terminals with licensed drivers and flight tracking.",
    reservationName: "Taxi booking from Maidenhead to Heathrow",
  },
  hero: {
    eyebrow: "Maidenhead to Heathrow taxi route",
    h1: "Maidenhead to Heathrow Taxi - Fixed-Price Airport Transfers",
    subtitle: "SL6 airport transfers for Heathrow terminals 2, 3, 4 and 5.",
    body:
      "Book reliable taxis from Maidenhead to Heathrow with licensed drivers, flight tracking and secure online booking. Fixed upfront fares with no surge pricing.",
  },
  quote: {
    label: "Get instant quotes",
    note: "Free - Fast - No obligation",
    presetPickup: "Maidenhead, SL6",
    presetDropoff: "Heathrow Airport",
  },
  flags: [
    "Fixed fares agreed upfront - no meters",
    "Live flight tracking for Heathrow arrivals",
    "Licensed drivers, meet & greet, luggage-ready",
    "Saloon, MPV, 8-16 seat minibus & coach options",
  ],
  reviewPageKey: "/maidenhead-to-heathrow",
  reviewAriaLabel: "Maidenhead to Heathrow customer reviews",
  lhrStatusTitle: "Live Heathrow checks for Maidenhead to Heathrow taxis",
  floatingCta: "Book Maidenhead to Heathrow Taxi",
  mobileCta: "Book Maidenhead to Heathrow Taxi",
  supportNudge: "Need Maidenhead to Heathrow help?",
  sections: [
    {
      id: "route-overview",
      title: "Maidenhead to Heathrow at a glance",
      body:
        "A key M4 airport corridor served 24/7 by CheapCabGrab. Ideal for Maidenhead residents, station pickups, riverside hotels, business travellers and families heading to Heathrow.",
      render: () => cardGrid("route-overview-grid", [
        {
          title: "Journey basics",
          items: [
            "Distance: Approx. 16-22 miles depending on pickup point",
            "Typical time: 25-45 minutes",
            "Typical saloon fare: from GBP45-GBP70",
            "Areas covered: Maidenhead, SL6, Cookham, Bray, Taplow and nearby villages",
          ],
        },
        {
          title: "Great for",
          items: [
            "Residents flying from Heathrow",
            "Hotel guests around Maidenhead and Bray",
            "Rail connections from Maidenhead station",
            "Families with luggage, child seats or early departures",
          ],
        },
        {
          title: "Why CheapCabGrab?",
          items: [
            "Licensed local drivers with Heathrow experience",
            "Transparent fares, no surge pricing",
            "Instant online booking and confirmation",
            "Receipts for business and expense claims",
          ],
        },
      ]),
    },
    {
      id: "route-tools",
      title: "Useful tools for your Maidenhead to Heathrow journey",
      body: "Plan every part of your airport trip with CheapCabGrab tools.",
      render: () => cardGrid("route-links-grid", [
        {
          title: "Heathrow Terminal Finder",
          body: "Not sure which terminal your airline uses? Use our live terminal finder to check Heathrow departure and arrival points.",
          link: { to: "/terminalFinder", label: "Open Terminal Finder", className: "route-link-btn" },
        },
        {
          title: "Airline contact search",
          body: "Quickly find customer service numbers, websites and support links for your airline before travelling.",
          link: { to: "/airlineContacts", label: "Find airline contacts", className: "route-link-btn" },
        },
        {
          title: "Minibus and coach options",
          body: "Travelling as a group from Maidenhead to Heathrow? Upgrade to a minibus or coach for extra luggage space and coordinated pickup.",
          links: [
            { to: "/minibus", label: "Heathrow minibus" },
            { to: "/coach", label: "Heathrow coach", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
      ]),
    },
    {
      id: "route-areas",
      title: "Maidenhead to Heathrow - popular pickup and drop-off points",
      body: "We regularly serve homes, hotels, stations and business locations across Maidenhead and the wider SL6 area.",
      render: () => cardGrid("route-areas-grid", [
        {
          title: "In and around Maidenhead",
          items: [
            "Maidenhead town centre",
            "Maidenhead railway station",
            "Bray, Taplow and Cookham",
            "Furze Platt, Cox Green and Pinkneys Green",
            "Riverside hotels and local business parks",
          ],
          link: { to: "/maidenhead-taxi", label: "Maidenhead taxis overview ->" },
        },
        {
          title: "Heathrow and nearby hotels",
          items: [
            "All Heathrow terminals (T2, T3, T4, T5)",
            "Airport hotels on Bath Road",
            "Hotels near M4 junctions and business parks",
          ],
          link: { to: "/heathrow-hotels", label: "See Heathrow hotel guide ->" },
        },
      ]),
    },
    {
      id: "route-faq",
      title: "Maidenhead to Heathrow taxi - FAQs",
      body: "A few common questions passengers ask when travelling between Maidenhead and Heathrow Airport.",
      render: () => (
        <div className="route-faq-grid">
          {[
            ["How long does a taxi from Maidenhead to Heathrow take?", "Most Maidenhead to Heathrow taxi journeys take around 25-45 minutes, depending on pickup point, terminal, traffic on the M4 and the time of day."],
            ["Can you pick up from Maidenhead railway station?", "Yes. Maidenhead station is a regular pickup point for Heathrow transfers, including passengers connecting from the Elizabeth line, GWR services and local journeys."],
            ["Do you track my incoming flight?", "Yes. For Heathrow pickups we monitor your flight and adjust the dispatch time where possible. If your flight is severely delayed or diverted, contact support and we will re-plan your pickup."],
            ["Can I book a larger vehicle from Maidenhead to Heathrow?", "Yes. For larger groups, luggage or work teams, we offer MPVs, 7-14 seater minibuses and 25+ seater coaches through our PSV partners. Use the quote form or visit our minibus and coach pages for more details."],
          ].map(([title, body]) => (
            <article className="route-faq-card" key={title}>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
          <div className="route-faq-footer">
            <p>Need more details? Visit our full <Link to="/faq">FAQ page</Link> or contact support.</p>
          </div>
        </div>
      ),
    },
    {
      id: "route-support",
      title: "Need help with a Maidenhead to Heathrow taxi booking?",
      body: "If you have special requirements, multi-stop journeys or corporate bookings, our team is happy to help.",
      render: () => (
        <div className="route-support-box">
          <p>Email: <a href="mailto:support@cheapcabgrab.com">support@cheapcabgrab.com</a></p>
          <p>Or send us a message via our <Link to="/contact">contact form</Link> and we will get back to you as soon as possible.</p>
          <p>
            For other popular routes, see our pages for{" "}
            <Link to="/maidenhead-taxi">Maidenhead taxis</Link>,{" "}
            <Link to="/heathrow-taxi">Heathrow taxis</Link> and our dedicated{" "}
            <Link to="/fleet">fleet</Link> and{" "}
            <Link to="/our-drivers">driver standards</Link> pages.
          </p>
        </div>
      ),
    },
  ],
};

const MaidenheadToHeathrow = () => (
  <CcgRoutePremiumPage route={maidenheadToHeathrowRoute} QuoteComponent={PostcodeQuotenew} />
);

export default MaidenheadToHeathrow;
